import { rem } from "@certn/cascadia";
import {
  ButtonGroup,
  Card,
  Divider,
  GhostButton,
  Icon,
  Text,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { StatusTag } from "./StatusTag";
import { GroupDropdownMenu } from "../GroupDropdownMenu";

interface Props {
  id: string;
  name: string | null;
  userCount: number;
  applicantFacingName: string | null;
  billingProfileName: string | null;
  isActive: boolean;
  isFavourite: boolean;
  mutate: () => void;
  onFavouriteClick: (id: string) => void;
}
export function TableDataCard(props: Props) {
  const {
    id,
    name,
    userCount,
    applicantFacingName,
    billingProfileName,
    isActive,
    isFavourite,
    mutate,
    onFavouriteClick,
  } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Card>
      <div
        className={styles.container}
        role="presentation"
        onClick={() => {
          navigate(`/group-overview?customer_group_id=${id}`, {
            state: {
              last: { href: `${location.pathname}${location.search}` },
            },
          });
        }}
        data-testid="sm-user-selectable-container"
      >
        <div className={styles.topHalf}>
          <div className={styles.userDetails}>
            <Text style="global/heading-medium" testID="sm-user-name">
              {name || "--"}
            </Text>
            <Text color="text-weak" testID="sm-user-email">
              {userCount} {t("members")}
            </Text>
          </div>
          <ButtonGroup>
            <div className={styles.favouriteButton}>
              <GhostButton
                size="small"
                onPress={() => {
                  onFavouriteClick(id);
                }}
              >
                <Icon
                  className={styles.iconSize}
                  name="star"
                  color="icon-weak"
                  padding="space-0"
                  fill={isFavourite}
                />
              </GhostButton>
            </div>
            <GroupDropdownMenu
              mutate={mutate}
              groupId={id}
              groupName={name || ""}
              isActive={isActive}
            />
          </ButtonGroup>
        </div>
        <div className={styles.bottomHalf}>
          <div className={css(styles.column, { flexBasis: "60%" })}>
            <Text style="global/label-small" color="text-weak">
              {t("Applicant Facing Name")}
            </Text>
            <Text>{applicantFacingName}</Text>
            <Divider hideBorder marginBottom="space-3" />
            <Text style="global/label-small" color="text-weak">
              {t("Status")}
            </Text>
            <Text>
              <StatusTag isActive={isActive} />
            </Text>
          </div>
          <div className={styles.column}>
            <Text style="global/label-small" color="text-weak">
              {t("Billing profile")}
            </Text>
            <Text>{billingProfileName}</Text>
          </div>
        </div>
      </div>
    </Card>
  );
}

const styles = {
  container: css({
    padding: "var(--space-4)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
    borderRadius: "var(--border-radius-3)",
    cursor: "pointer",
  }),
  topHalf: css({
    display: "flex",
    justifyContent: "space-between",
  }),
  userDetailsContainer: css({
    display: "flex",
    alignItems: "center",
    gap: 10,
  }),
  userDetails: css({
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
  }),
  bottomHalf: css({
    display: "flex",
    flexDirection: "row",
    gap: "var(--space-2)",
  }),
  column: css({
    display: "flex",
    flexDirection: "column",
  }),
  iconSize: css({
    width: rem("space-5"),
    height: rem("space-5"),
  }),
  favouriteButton: css({
    zIndex: 10000,
  }),
};
